<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal">
      <div class="modal-header">
        <h2 class="modal-title">New Task</h2>
        <button class="modal-close" @click="$emit('close')">×</button>
      </div>

      <div class="form-fields">
        <!-- Title -->
        <div class="form-group">
          <label class="form-label">Title</label>
          <input
            v-model="form.title"
            class="form-control"
            :class="{ error: errors.title }"
            placeholder="Enter task title…"
            @input="errors.title = ''"
          />
          <span v-if="errors.title" class="error-msg">{{ errors.title }}</span>
        </div>

        <!-- Due Date -->
        <div class="form-group">
          <label class="form-label">Due Date</label>
          <input
            v-model="form.due_date"
            type="date"
            class="form-control"
            :class="{ error: errors.due_date }"
            :min="today"
            @change="errors.due_date = ''"
          />
          <span v-if="errors.due_date" class="error-msg">{{ errors.due_date }}</span>
        </div>

        <!-- Priority -->
        <div class="form-group">
          <label class="form-label">Priority</label>
          <div class="priority-options">
            <button
              v-for="p in priorities"
              :key="p.value"
              type="button"
              class="priority-opt"
              :class="[`opt-${p.value}`, { selected: form.priority === p.value }]"
              @click="form.priority = p.value; errors.priority = ''"
            >
              <span class="opt-dot"></span>
              {{ p.label }}
            </button>
          </div>
          <span v-if="errors.priority" class="error-msg">{{ errors.priority }}</span>
        </div>

        <!-- API Error -->
        <div v-if="apiError" class="api-error">{{ apiError }}</div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-ghost" @click="$emit('close')">Cancel</button>
        <button class="btn btn-primary" :disabled="submitting" @click="submit">
          <span v-if="submitting" class="btn-spinner"></span>
          {{ submitting ? 'Creating…' : 'Create Task' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'

export default {
  name: 'CreateTaskModal',
  emits: ['close', 'created'],
  data() {
    return {
      form: { title: '', due_date: '', priority: 'medium' },
      errors: {},
      apiError: '',
      submitting: false,
      priorities: [
        { label: 'High', value: 'high' },
        { label: 'Medium', value: 'medium' },
        { label: 'Low', value: 'low' }
      ]
    }
  },
  computed: {
    today() {
      return new Date().toISOString().split('T')[0]
    }
  },
  methods: {
    validate() {
      const e = {}
      if (!this.form.title.trim()) e.title = 'Title is required'
      if (!this.form.due_date) e.due_date = 'Due date is required'
      if (!this.form.priority) e.priority = 'Please select a priority'
      this.errors = e
      return Object.keys(e).length === 0
    },
    async submit() {
      if (!this.validate()) return
      this.submitting = true
      this.apiError = ''
      try {
        await api.createTask(this.form)
        this.$emit('created')
      } catch (err) {
        if (err.response?.status === 422) {
          const errs = err.response.data.errors || {}
          this.errors = Object.fromEntries(
            Object.entries(errs).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])
          )
        } else {
          this.apiError = err.response?.data?.message || 'Something went wrong'
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.form-fields { display: flex; flex-direction: column; gap: 1.2rem; }

.priority-options { display: flex; gap: 8px; }

.priority-opt {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text-muted);
  font-family: 'DM Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.opt-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.opt-high .opt-dot { background: var(--high); }
.opt-medium .opt-dot { background: var(--medium); }
.opt-low .opt-dot { background: var(--low); }

.priority-opt:hover { border-color: var(--text-muted); color: var(--text); }
.opt-high.selected { border-color: var(--high); color: var(--high); background: rgba(248,113,113,0.08); }
.opt-medium.selected { border-color: var(--medium); color: var(--medium); background: rgba(251,191,36,0.08); }
.opt-low.selected { border-color: var(--low); color: var(--low); background: rgba(52,211,153,0.08); }

.api-error {
  padding: 10px 14px;
  border-radius: var(--radius-sm);
  background: rgba(248,113,113,0.1);
  border: 1px solid rgba(248,113,113,0.3);
  color: var(--danger);
  font-size: 0.875rem;
}

.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: white;
  border-radius: 50%;
  display: inline-block;
  animation: spin 0.7s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg) } }
</style>